import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { VideoPlayer } from "../components/VideoPlayer";
import { JsonLd, BreadcrumbJsonLd } from "../components/JsonLd";

const SITE = "https://fedevo20.fr";

const mp4ContentUrl = (video) => {
  if (video.source_type !== "mp4" || !video.source_url) return undefined;
  if (video.source_url.startsWith("/api/")) return `${API}${video.source_url.replace(/^\/api/, "")}`;
  return video.source_url;
};

export default function Watch() {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [related, setRelated] = useState([]);
  const [channel, setChannel] = useState(null);

  useEffect(() => {
    api.get(`/videos/${id}`).then(r => {
      setVideo(r.data);
      api.get(`/channels/${r.data.channel_id}`).then(rc => setChannel(rc.data)).catch(() => {});
      api.get(`/videos?channel_id=${r.data.channel_id}`).then(r2 => setRelated(r2.data.filter(v => v.id !== id)));
    });
  }, [id]);

  // Update <title> + og:title dynamically per video for better SEO preview
  useEffect(() => {
    if (!video) return;
    const prev = document.title;
    document.title = `${video.title} · Fedevo 2.0`;
    return () => { document.title = prev; };
  }, [video]);

  const jsonLd = video ? {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": video.title,
    "description": video.description || `${video.title} — diffusé sur Fedevo 2.0, média urbain spécialisé streaming.`,
    "thumbnailUrl": video.thumbnail ? [video.thumbnail] : undefined,
    "uploadDate": video.created_at,
    "contentUrl": mp4ContentUrl(video),
    "embedUrl": video.youtube_id ? `https://www.youtube.com/embed/${video.youtube_id}` : undefined,
    "publisher": {
      "@type": "Organization",
      "name": "Fedevo 2.0 · Urban Media",
      "logo": { "@type": "ImageObject", "url": "https://customer-assets.emergentagent.com/job_tv-broadcast-4/artifacts/p88vdzfy_fedevo%20logo%20tv%20jaune.png" }
    },
    "isPartOf": channel ? { "@type": "BroadcastService", "name": channel.name } : undefined,
    "inLanguage": "fr",
    "url": `${SITE}/watch/${video.id}`,
  } : null;

  if (!video) return <div className="p-10 text-center text-[color:var(--text-muted)]">Chargement...</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-6 grid lg:grid-cols-[1fr_380px] gap-6">
      <JsonLd data={jsonLd} />
      <BreadcrumbJsonLd items={[
        { name: "Accueil", path: "/" },
        { name: "Chaînes", path: "/channels" },
        ...(channel ? [{ name: channel.name, path: `/channel/${channel.id}` }] : []),
        { name: video.title, path: `/watch/${video.id}` },
      ]} />
      <div>
        <div className="rounded-xl overflow-hidden border border-[color:var(--border)]">
          <VideoPlayer video={video} autoplay />
        </div>
        <h1 className="font-display text-4xl mt-4">{video.title}</h1>
        <p className="text-sm text-[color:var(--text-muted)] mt-2 max-w-3xl">{video.description}</p>
      </div>
      <aside className="space-y-3">
        <h2 className="font-display text-xl mb-2">Plus sur cette chaîne</h2>
        {related.map(v => (
          <Link key={v.id} to={`/watch/${v.id}`} className="flex gap-3 p-2 rounded-lg hover:bg-[color:var(--bg-elev-2)] transition-colors" data-testid={`related-${v.id}`}>
            <img src={v.thumbnail} alt="" className="w-32 h-20 object-cover rounded" />
            <div className="flex-1">
              <div className="text-sm line-clamp-2">{v.title}</div>
            </div>
          </Link>
        ))}
      </aside>
    </div>
  );
}
